import React, { useEffect, useState } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, X } from 'lucide-react';
import api from '../../utils/api';

interface Outcome {
  id: string;
  studentLabel: string;
  result: string;
  subject: string | null;
  isVisible: boolean;
  sortOrder: number;
  createdAt?: string;
}

const emptyDraft = { studentLabel: '', result: '', subject: '' };

const OutcomesManager: React.FC = () => {
  const [rows, setRows] = useState<Outcome[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{ studentLabel: string; result: string; subject: string }>(emptyDraft);
  const [showAdd, setShowAdd] = useState(false);
  const [addDraft, setAddDraft] = useState(emptyDraft);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/outcomes/all');
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load outcomes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const beginEdit = (row: Outcome) => {
    setEditingId(row.id);
    setEditDraft({
      studentLabel: row.studentLabel || '',
      result: row.result || '',
      subject: row.subject || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(emptyDraft);
  };

  const saveEdit = async (id: string) => {
    if (!editDraft.studentLabel.trim() || !editDraft.result.trim()) {
      window.alert('Student label and result are required');
      return;
    }
    setBusy(true);
    try {
      await api.put(`/outcomes/${id}`, {
        studentLabel: editDraft.studentLabel,
        result: editDraft.result,
        subject: editDraft.subject || null,
      });
      cancelEdit();
      await load();
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Failed to save outcome');
    } finally {
      setBusy(false);
    }
  };

  const toggleVisible = async (row: Outcome) => {
    try {
      await api.put(`/outcomes/${row.id}`, { isVisible: !row.isVisible });
      await load();
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Failed to update visibility');
    }
  };

  const handleDelete = async (row: Outcome) => {
    if (!window.confirm(`Delete outcome for ${row.studentLabel || 'this student'}? This cannot be undone.`)) return;
    try {
      await api.delete(`/outcomes/${row.id}`);
      await load();
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Failed to delete outcome');
    }
  };

  const handleAdd = async () => {
    if (!addDraft.studentLabel.trim() || !addDraft.result.trim()) {
      window.alert('Student label and result are required');
      return;
    }
    setBusy(true);
    try {
      await api.post('/outcomes', {
        studentLabel: addDraft.studentLabel,
        result: addDraft.result,
        subject: addDraft.subject || null,
        sortOrder: rows.length,
        isVisible: true,
      });
      setAddDraft(emptyDraft);
      setShowAdd(false);
      await load();
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Failed to add outcome');
    } finally {
      setBusy(false);
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= rows.length) return;
    const newOrder = [...rows];
    const [item] = newOrder.splice(index, 1);
    newOrder.splice(next, 0, item);
    setRows(newOrder);
    try {
      await api.post('/outcomes/reorder', { order: newOrder.map(r => r.id) });
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Failed to reorder');
      await load();
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-h3 text-ink mb-2">Outcomes</h2>
          <p className="text-sm text-muted">Student results. Shown on the home page in the order below.</p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(s => !s)}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add outcome</span>
        </button>
      </div>

      {showAdd && (
        <div className="card mb-6">
          <h3 className="font-serif text-lg text-ink mb-4">New outcome</h3>
          <div className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-ink mb-1">Student label</label>
                <input
                  className="input"
                  value={addDraft.studentLabel}
                  onChange={e => setAddDraft(d => ({ ...d, studentLabel: e.target.value }))}
                  placeholder="e.g. Year 6, Sutton"
                />
              </div>
              <div>
                <label className="block text-sm text-ink mb-1">Subject (optional)</label>
                <input
                  className="input"
                  value={addDraft.subject}
                  onChange={e => setAddDraft(d => ({ ...d, subject: e.target.value }))}
                  placeholder="e.g. English"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-ink mb-1">Result</label>
              <input
                className="input"
                value={addDraft.result}
                onChange={e => setAddDraft(d => ({ ...d, result: e.target.value }))}
                placeholder="e.g. Offer from Whitgift School"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={() => { setShowAdd(false); setAddDraft(emptyDraft); }}
                className="btn btn-secondary"
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAdd}
                className="btn btn-primary"
                disabled={busy}
              >
                {busy ? 'Saving...' : 'Save outcome'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-muted text-sm">Loading...</p>
      ) : rows.length === 0 ? (
        <div className="card text-center">
          <p className="text-muted text-sm">No outcomes yet. Add the first one above.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((row, i) => {
            const isEditing = editingId === row.id;
            return (
              <div key={row.id} className="card">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      className="p-1 text-muted hover:text-ink disabled:opacity-30"
                      title="Move up"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(i, 1)}
                      disabled={i === rows.length - 1}
                      className="p-1 text-muted hover:text-ink disabled:opacity-30"
                      title="Move down"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer">
                      <input
                        type="checkbox"
                        checked={row.isVisible}
                        onChange={() => toggleVisible(row)}
                      />
                      <span>Visible</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => handleDelete(row)}
                      className="p-1 text-muted hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-3">
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm text-ink mb-1">Student label</label>
                        <input
                          className="input"
                          value={editDraft.studentLabel}
                          onChange={e => setEditDraft(d => ({ ...d, studentLabel: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-ink mb-1">Subject (optional)</label>
                        <input
                          className="input"
                          value={editDraft.subject}
                          onChange={e => setEditDraft(d => ({ ...d, subject: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-ink mb-1">Result</label>
                      <input
                        className="input"
                        value={editDraft.result}
                        onChange={e => setEditDraft(d => ({ ...d, result: e.target.value }))}
                      />
                    </div>
                    <div className="flex gap-2 justify-end pt-2">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="btn btn-secondary inline-flex items-center gap-2"
                        disabled={busy}
                      >
                        <X className="w-4 h-4" /> Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => saveEdit(row.id)}
                        className="btn btn-primary inline-flex items-center gap-2"
                        disabled={busy}
                      >
                        <Save className="w-4 h-4" />
                        {busy ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-ink font-medium">{row.result}</p>
                    <p className="text-sm text-muted mt-1">
                      {row.studentLabel}
                      {row.subject ? ` · ${row.subject}` : ''}
                    </p>
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => beginEdit(row)}
                        className="btn btn-secondary text-sm"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OutcomesManager;
